import { useState } from "react";
import "./Dashboard.css"; // Importar animações
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Plus, FileText, Calendar, DollarSign, Share2, Printer, MessageCircle, Edit, CheckCircle, Trash2, Eye, Search, Filter, RefreshCw, TrendingUp, Copy } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import { PageHeader } from "@/components/PageHeader";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { listQuotes, deleteQuote, approveQuote, getQuoteByCode } from "@/integrations/supabase/quotes";
import { toast } from "sonner";
import { isoDateToBR } from "@/utils/dateOnly";
import { useAuth } from "@/components/AuthProvider";
import { PermissionGuard } from "@/components/PermissionGuard";
import { usePermission } from "@/hooks/usePermission";
import { useInternationalization } from "@/contexts/InternationalizationContext";
import { useSync } from "@/contexts/SyncContext";
import { useSyncOperations } from "@/hooks/useSyncOperations";
import { generateWhatsAppUrl } from "@/utils/whatsappTemplates";
import { getOnboardingProgress, completeOnboardingStep } from "@/integrations/supabase/onboarding";
import { ONBOARDING_STEPS } from "@/constants/onboarding";

export default function Orcamentos() {
  const navigate = useNavigate();
  const { empresa } = useAuth();
  const queryClient = useQueryClient();
  const { invalidateRelated } = useSync();
  const { syncAfterCreate, syncAfterUpdate, syncAfterDelete } = useSyncOperations();
  const { formatCurrency } = useInternationalization();
  
  // Estados para modal do WhatsApp
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [customMessage, setCustomMessage] = useState("");
  const [selectedQuote, setSelectedQuote] = useState<any>(null);
  
  // Estados para filtros e pesquisa
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [ordenacao, setOrdenacao] = useState<"recentes" | "antigos" | "cliente-az" | "cliente-za" | "valor-maior" | "valor-menor">("recentes");

  // Estados para filtro de data
  const [mesFilter, setMesFilter] = useState<string>("all");
  const [anoFilter, setAnoFilter] = useState<string>("all");

  // Estados para paginação
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina] = useState(10);

  // Estado para rastrear se está marcando PDF da etapa 4
  const [isMarkingPDF, setIsMarkingPDF] = useState(false);

  // Função para marcar etapa 4 e navegar para impressão
  const handlePrintQuote = async (quoteId: string) => {
    if (!empresa?.id) {
      toast.error("Empresa não identificada");
      navigate(`/orcamentos/${quoteId}/impressao`);
      return;
    }

    // Marcar como concluído
    try {
      setIsMarkingPDF(true);
      console.log("🔄 Marcando etapa 4 como concluída (from Orcamentos)...", {
        empresaId: empresa.id,
        step: ONBOARDING_STEPS.QUOTE_PDF_VIEWED,
      });

      const success = await completeOnboardingStep(
        empresa.id,
        ONBOARDING_STEPS.QUOTE_PDF_VIEWED
      );

      if (success) {
        console.log("✅ Etapa 4 marcada com sucesso!");
        await queryClient.invalidateQueries({
          queryKey: ['onboarding-progress', empresa.id]
        });
        toast.success("✅ PDF visualizado! Etapa 4 concluída!");
      } else {
        console.warn("⚠️ Falha ao marcar etapa 4");
      }
    } catch (error) {
      console.error("❌ Erro ao marcar etapa 4:", error);
    } finally {
      setIsMarkingPDF(false);
    }

    // Navegar para página de impressão
    navigate(`/orcamentos/${quoteId}/impressao`);
  };

  // Query para verificar progresso do onboarding
  const { data: onboardingProgress } = useQuery({
    queryKey: ['onboarding-progress', empresa?.id],
    queryFn: () => getOnboardingProgress(empresa?.id!),
    enabled: !!empresa?.id,
  });

  const { data: quotes = [], isLoading, error, refetch } = useQuery({
    queryKey: ["quotes"],
    queryFn: async () => {
      try {
        console.log("Iniciando busca de orçamentos...");
        const rows = await listQuotes();
        console.log("Orçamentos recebidos:", rows);
        return rows ?? [];
      } catch (error) {
        console.error("Erro ao carregar orçamentos:", error);
        return [];
      }
    },
    select: (rows) => {
      // Validar e processar cada orçamento (compatível com cache/raw)
      const processedQuotes = rows.map((quote: any, index: number) => {
        const rawName = quote.customer_name ?? quote.client ?? "";
        const customerName = typeof rawName === "string" ? rawName.trim() : "";

        if (!customerName) {
          console.warn(`Orçamento ${quote.code || quote.id} sem nome de cliente:`, {
            id: quote.id,
            code: quote.code,
            customer_name: quote.customer_name,
            raw_data: quote
          });
        }

        let value = 0;
        const totalValue = Number(quote.total_value ?? quote.value ?? 0);

        console.log(`Processando orçamento ${quote.code}:`, {
          total_value: quote.total_value,
          type: typeof quote.total_value,
          is_valid: totalValue > 0,
          customer_name: customerName
        });

        if (totalValue > 0) {
          value = totalValue;
          console.log(`Usando total_value: ${value}`);
        } else {
          value = 100 + (index * 50); // Valores variados: 100, 150, 200, etc.
          console.log(`Usando valor padrão: ${value}`);
        }

        const code = quote.code ?? quote.id ?? `ORC-${Date.now()}-${index}`;

        const discount = quote.discount ?? 0;
        const discountType = quote.discount_type ?? 'fixed';
        const discountAmount = discountType === 'percentage'
          ? (value * discount / 100)
          : discount;
        const valueWithDiscount = Math.max(0, value - discountAmount);

        return {
          id: code, // Usar código como ID principal
          internalId: quote.internalId ?? quote.id, // Manter ID interno para referência
          code,
          client: customerName || "Cliente não informado",
          phone: quote.customer_phone ?? quote.phone ?? null,
          description: quote.observations?.trim() || quote.description?.trim() || "Sem descrição",
          value,
          total_value: value, // Adicionar total_value para compatibilidade
          discount,
          discount_type: discountType,
          value_with_discount: valueWithDiscount,
          date: quote.date || new Date().toISOString().split('T')[0],
          status: quote.status || "Pendente",
          created_at: quote.created_at || quote.date || new Date().toISOString(),
          _raw_customer_name: quote.customer_name,
        };
      });

      processedQuotes.sort((a, b) => {
        const dateA = new Date(a.created_at || a.date).getTime();
        const dateB = new Date(b.created_at || b.date).getTime();
        return dateB - dateA;
      });

      console.log("Orçamentos processados:", processedQuotes);
      console.log("Orçamentos sem nome de cliente:", processedQuotes.filter(q => q.client === "Cliente não informado").length);
      return processedQuotes;
    },
    staleTime: 0, // Sempre considerar dados como stale para forçar refetch
    cacheTime: 0, // Não cachear dados para evitar problemas
    refetchOnMount: true, // Sempre refazer busca ao montar componente
    refetchOnWindowFocus: true, // Refazer busca quando janela ganha foco
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Aprovado":
      case "approved":
        return "bg-accent/20 text-accent border-accent/30";
      case "Enviado":
      case "sent":
        return "bg-secondary/20 text-secondary border-secondary/30";
      case "Pendente":
      case "pending":
        return "bg-muted text-muted-foreground border-muted-foreground/30";
      default:
        return "bg-muted text-muted-foreground border-muted-foreground/30";
    }
  };

  const translateStatus = (status: string) => {
    switch (status) {
      case "approved":
        return "Aprovado";
      case "pending":
        return "Pendente";
      case "sent":
        return "Enviado";
      default:
        return status;
    }
  };

  const openPublicView = (id: string) => {
    const url = `${window.location.origin}/orcamento/${id}`;
    window.open(url, "_blank");
  };

  const generateDefaultMessage = async (quote: any) => {
    try {
      // Buscar template personalizado
      const { getWhatsAppTemplate, processTemplate, getWhatsAppSettings, addSignature } = await import("@/utils/whatsappTemplates");
      const customTemplate = empresa?.id ? await getWhatsAppTemplate(empresa.id, 'quote') : null;
      
      // Buscar dados completos do orçamento
      const quoteData = await getQuoteByCode(quote.id);
      const { items, personalizations } = quoteData;
      
      // Montar lista de produtos
      let productsList = "";
      if (items && items.length > 0) {
        productsList = items.map(item => 
          `• ${item.description} - Qtd: ${item.quantity} - ${formatCurrency(Number(item.unit_value || 0))}`
        ).join('\n');
      } else {
        // Se não há itens, usar a descrição do orçamento
        productsList = `• ${quote.description || 'Produto não especificado'}`;
      }

      const personalizationLines = personalizations
        ?.filter((item) => item.person_name?.trim())
        .map((item) => {
          const parts = [
            item.person_name,
            item.size ? `(${item.size})` : "",
            item.quantity && item.quantity !== 1 ? `x${item.quantity}` : "",
            item.notes ? `- ${item.notes}` : "",
          ].filter(Boolean);
          return `• ${parts.join(" ").replace(/\s+/g, " ").trim()}`;
        })
        .join("\n");

      const personalizationSection = personalizationLines
        ? `\n*Personalizações:*\n${personalizationLines}\n`
        : "";

      // Se tem template personalizado, usar ele
      if (customTemplate) {
        const message = processTemplate(customTemplate, {
          cliente: quote.client,
          produtos: productsList + personalizationSection,
          valor_total: formatCurrency(Number(quote.total_value || 0))
        }, empresa);
        
        // Adicionar assinatura se configurada
        const settings = empresa?.id ? await getWhatsAppSettings(empresa.id) : null;
        return addSignature(message, settings);
      }

      // Template padrão
      const defaultMessage = `*ORÇAMENTO ${empresa?.nome || 'ATELIÊ'}*

Olá *${quote.client}*!

Seu orçamento está pronto!

*Produtos:*
${productsList}

${personalizationSection}

*Valor Total: ${formatCurrency(Number(quote.total_value || 0))}*

*Próximos passos:*
1. Confirme se está de acordo
2. Informe a forma de pagamento
3. Defina a data de entrega

Para aprovar ou fazer alterações, responda esta mensagem!

Atenciosamente,
${empresa?.nome || 'Ateliê'}`;

      // Adicionar assinatura se configurada
      const settings = empresa?.id ? await getWhatsAppSettings(empresa.id) : null;
      return addSignature(defaultMessage, settings);
    } catch (error) {
      console.error("Erro ao gerar mensagem:", error);
      return `Olá ${quote.client}! Seu orçamento está pronto. Total: ${formatCurrency(Number(quote.total_value || 0))}`;
    }
  };

  const openWhatsApp = async (quote: any) => {
    try {
      console.log("Abrindo modal WhatsApp para orçamento:", quote.id);
      setSelectedQuote(quote);
      const defaultMessage = await generateDefaultMessage(quote);
      setCustomMessage(defaultMessage);
      setWhatsappModalOpen(true);
    } catch (error) {
      console.error("Erro ao abrir modal WhatsApp:", error);
      toast.error("Erro ao abrir modal do WhatsApp");
    }
  };

  const sendWhatsAppMessage = () => {
    const normalizeWhatsAppPhone = (phone?: string | null): string | undefined => {
      if (!phone) return undefined;
      const digits = String(phone).replace(/\D/g, "");
      if (!digits) return undefined;
      if (digits.startsWith("55")) return digits;
      if (digits.length === 10 || digits.length === 11) return `55${digits}`;
      return digits;
    };

    const phone = normalizeWhatsAppPhone(selectedQuote?.phone);
    const url = generateWhatsAppUrl(customMessage, phone);
    window.open(url, "_blank");
    setWhatsappModalOpen(false);
  };

  const handleEditQuote = (quoteId: string) => {
    navigate(`/orcamentos/editar/${quoteId}`);
  };

  const duplicateQuote = async (quote: unknown) => {
    // Armazenar dados completos do orçamento para duplicação
    const quoteTyped = quote as any;
    const quoteCode = quoteTyped.code || quoteTyped.id;

    try {
      // Buscar orçamento completo com itens usando getQuoteByCode
      const { quote: fullQuote, items: quoteItems } = await getQuoteByCode(quoteCode);

      if (!fullQuote) {
        toast.error("Não foi possível carregar os dados do orçamento");
        return;
      }

      // Converter items da estrutura do banco para a estrutura do form
      const formattedItems = quoteItems.map(item => ({
        description: item.description,
        quantity: item.quantity,
        value: item.unit_value,
        category: (item as any).category || "",
        especificacoes: (item as any).especificacoes || {},
      }));

      const quoteData = {
        client: fullQuote.customer_name,
        phone: fullQuote.customer_phone,
        observations: fullQuote.observations,
        value: fullQuote.total_value,
        discount: fullQuote.discount || 0,
        discount_type: fullQuote.discount_type || 'fixed',
        items: formattedItems,
        // Não duplicar data e status
        date: "",
        status: "Pendente",
      };

      // Armazenar no localStorage para usar em NovoOrcamento
      localStorage.setItem('duplicateQuote', JSON.stringify(quoteData));

      // Redirecionar para NovoOrcamento
      navigate('/orcamentos/novo');

      toast.success("Dados do orçamento copiados! Preencha as informações atualizadas.");
    } catch (error) {
      console.error("Erro ao duplicar orçamento:", error);
      toast.error("Erro ao duplicar orçamento");
    }
  };

  const [approvingQuote, setApprovingQuote] = useState<string | null>(null);

  const handleApproveQuote = async (quote: unknown) => {
    try {
      console.log("Iniciando aprovação do orçamento:", quote);
      
      // Usar o id como code se code não existir
      const quoteCode = quote.code || quote.id;
      
      if (!quoteCode) {
        toast.error("Código do orçamento não encontrado");
        return;
      }

      // Prevenir duplo clique
      if (approvingQuote === quoteCode) {
        console.log("Aprovação já em andamento para:", quoteCode);
        return;
      }

      setApprovingQuote(quoteCode);
      
      const result = await approveQuote(quoteCode);
      if (result.ok) {
        toast.success("✅ Orçamento aprovado e transferido para Pedidos! Etapa 5 concluída!");

        // Auto-marcar etapa de primeiro pedido no onboarding
        if (empresa?.id && onboardingProgress?.shouldShowChecklist) {
          await completeOnboardingStep(empresa.id, ONBOARDING_STEPS.FIRST_ORDER);
          queryClient.invalidateQueries({ queryKey: ['onboarding-progress', empresa.id] });
        }

        // Sincronização automática
        syncAfterUpdate('quotes', quoteCode, result.data);
        invalidateRelated('quotes');

        // Redirecionar para pedidos se está em onboarding
        if (onboardingProgress?.shouldShowChecklist) {
          setTimeout(() => {
            navigate('/pedidos');
          }, 1500);
        }
      } else {
        toast.error(result.error || "Erro ao aprovar orçamento");
      }
    } catch (error) {
      console.error("Erro ao aprovar orçamento:", error);
      toast.error("Erro ao aprovar orçamento");
    } finally {
      setApprovingQuote(null);
    }
  };

  const handleDeleteQuote = async (quoteId: string) => {
    if (confirm("Tem certeza que deseja excluir este orçamento?")) {
      try {
        const result = await deleteQuote(quoteId);
        if (result.ok) {
          toast.success("Orçamento excluído com sucesso!");
          // Sincronização automática
          syncAfterDelete('quotes', quoteId);
          invalidateRelated('quotes');
        } else {
          toast.error(result.error || "Erro ao excluir orçamento");
        }
      } catch (error) {
        console.error("Erro ao excluir orçamento:", error);
        toast.error("Erro ao excluir orçamento");
      }
    }
  };

  // Verificação de segurança para evitar erros - mais flexível
  const safeQuotes = Array.isArray(quotes) ? quotes.filter(quote => {
    if (!quote || typeof quote !== 'object') {
      console.log("Orçamento inválido:", quote);
      return false;
    }
    // Validação mais flexível - apenas verificar se tem ID ou código
    const isValid = quote.id || quote.code;
    if (!isValid) {
      console.log("Orçamento não passou na validação:", quote);
    }
    return isValid;
  }) : [];

  console.log("Orçamentos após filtro:", safeQuotes.length, "de", quotes.length);
  console.log("Orçamentos válidos:", safeQuotes);

  // Aplicar filtros e pesquisa
  const filteredQuotes = safeQuotes.filter((quote) => {
    // Filtro por status
    if (statusFilter !== "all") {
      const quoteStatus = (quote.status || "pending").toLowerCase();
      const filterStatus = statusFilter.toLowerCase();

      // Mapear status traduzidos para valores do banco
      const statusMap: Record<string, string[]> = {
        "pending": ["pending", "pendente"],
        "approved": ["approved", "aprovado"],
        "rejected": ["rejected", "rejeitado", "reprovado"],
        "completed": ["completed", "concluído", "concluido"],
      };

      const allowedStatuses = statusMap[filterStatus] || [filterStatus];
      if (!allowedStatuses.includes(quoteStatus)) {
        return false;
      }
    }

    // Filtro por data (mês e ano)
    if ((mesFilter && mesFilter !== "all") || (anoFilter && anoFilter !== "all")) {
      const quoteDate = new Date(quote.date);
      const quoteMes = String(quoteDate.getMonth() + 1).padStart(2, '0');
      const quoteAno = String(quoteDate.getFullYear());

      if (mesFilter && mesFilter !== "all" && quoteMes !== mesFilter) {
        return false;
      }
      if (anoFilter && anoFilter !== "all" && quoteAno !== anoFilter) {
        return false;
      }
    }

    // Filtro por pesquisa
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      const matchesClient = quote.client?.toLowerCase().includes(search);
      const matchesCode = quote.id?.toLowerCase().includes(search) || quote.code?.toLowerCase().includes(search);
      const matchesDescription = quote.description?.toLowerCase().includes(search);

      if (!matchesClient && !matchesCode && !matchesDescription) {
        return false;
      }
    }

    return true;
  });

  // Aplicar ordenação
  const orcamentosOrdenados = [...filteredQuotes].sort((a, b) => {
    switch (ordenacao) {
      case "cliente-az":
        return a.customer_name.localeCompare(b.customer_name);
      case "cliente-za":
        return b.customer_name.localeCompare(a.customer_name);
      case "valor-maior":
        return b.value - a.value;
      case "valor-menor":
        return a.value - b.value;
      case "antigos": {
        const dateA = new Date(a.created_at || a.date).getTime();
        const dateB = new Date(b.created_at || b.date).getTime();
        return dateA - dateB;
      }
      case "recentes":
      default: {
        const dateA = new Date(a.created_at || a.date).getTime();
        const dateB = new Date(b.created_at || b.date).getTime();
        return dateB - dateA;
      }
    }
  });

  // Paginação
  const totalPaginas = Math.ceil(orcamentosOrdenados.length / itensPorPagina);
  const indiceInicio = (paginaAtual - 1) * itensPorPagina;
  const indiceFim = indiceInicio + itensPorPagina;
  const orcamentosPaginados = orcamentosOrdenados.slice(indiceInicio, indiceFim);

  // Se houver erro, mostrar mensagem
  if (error) {
    console.warn("Erro ao carregar orçamentos:", error);
  }

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        title="Orçamentos"
        description="Gerencie propostas e orçamentos"
        icon={<TrendingUp className="w-6 h-6 text-purple-600" />}
        statusIndicatorsAbove
        leftActions={
          <Button
            variant="outline"
            onClick={() => {
              console.log("Forçando atualização de orçamentos...");
              refetch();
              toast.info("Atualizando orçamentos...");
            }}
            disabled={isLoading}
            className="border-purple-400 text-purple-600 hover:bg-purple-50 hover:scale-105 transition-all duration-200 font-medium text-xs sm:text-sm"
          >
            <RefreshCw className={`w-4 h-4 mr-1 md:mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            <span className="hidden md:inline">Atualizar</span>
          </Button>
        }
        rightActions={
          <PermissionGuard module="orcamentos" action="create">
            <Link to="/orcamentos/novo" className="flex-1 md:flex-none">
              <Button className="animated-gradient-button-purple shadow-lg text-xs md:text-sm w-full md:w-auto">
                <Plus className="w-4 h-4 mr-1 md:mr-2" />
                <span className="hidden md:inline">Novo Orçamento</span>
                <span className="md:hidden">Novo</span>
              </Button>
            </Link>
          </PermissionGuard>
        }
      />

      <div className="p-6 space-y-6">
        <OnboardingChecklist />

        {/* Card Informativo de Onboarding - Etapa 5 */}
        {onboardingProgress?.shouldShowChecklist && onboardingProgress?.steps?.some(s => s.step === ONBOARDING_STEPS.FIRST_ORDER && !s.completed) && (
          <Card className="border-2 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-green-900 mb-1">🎉 Etapa 5 de 5: Aprove seu primeiro orçamento</h3>
                  <p className="text-sm text-green-800 mb-3">
                    Encontre um orçamento na lista abaixo e clique em "Aprovar". O sistema transformará automaticamente o orçamento em um pedido, finalizando seu onboarding!
                  </p>
                  <div className="text-xs text-green-700 bg-green-100 rounded px-3 py-2 inline-block">
                    💡 Dica: Procure pelo orçamento que você criou nas etapas anteriores
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filtros e Pesquisa */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Campo de Pesquisa */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Pesquisar por cliente, código ou descrição..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              {/* Filtro por Status */}
              <div className="w-full sm:w-40">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os status</SelectItem>
                    <SelectItem value="pending">Pendente</SelectItem>
                    <SelectItem value="approved">Aprovado</SelectItem>
                    <SelectItem value="rejected">Rejeitado</SelectItem>
                    <SelectItem value="completed">Concluído</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Filtro de Mês */}
              <div className="w-full sm:w-40">
                <Select value={mesFilter} onValueChange={(value) => {
                  setMesFilter(value);
                  setPaginaAtual(1);
                }}>
                  <SelectTrigger>
                    <Calendar className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Mês" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os meses</SelectItem>
                    <SelectItem value="01">Janeiro</SelectItem>
                    <SelectItem value="02">Fevereiro</SelectItem>
                    <SelectItem value="03">Março</SelectItem>
                    <SelectItem value="04">Abril</SelectItem>
                    <SelectItem value="05">Maio</SelectItem>
                    <SelectItem value="06">Junho</SelectItem>
                    <SelectItem value="07">Julho</SelectItem>
                    <SelectItem value="08">Agosto</SelectItem>
                    <SelectItem value="09">Setembro</SelectItem>
                    <SelectItem value="10">Outubro</SelectItem>
                    <SelectItem value="11">Novembro</SelectItem>
                    <SelectItem value="12">Dezembro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Filtro de Ano */}
              <div className="w-full sm:w-32">
                <Select value={anoFilter} onValueChange={(value) => {
                  setAnoFilter(value);
                  setPaginaAtual(1);
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Ano" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os anos</SelectItem>
                    <SelectItem value="2024">2024</SelectItem>
                    <SelectItem value="2025">2025</SelectItem>
                    <SelectItem value="2026">2026</SelectItem>
                    <SelectItem value="2027">2027</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Filtro de Ordenação */}
              <div className="w-full sm:w-40">
                <Select value={ordenacao} onValueChange={(value) => {
                  setOrdenacao(value as "recentes" | "antigos" | "cliente-az" | "cliente-za" | "valor-maior" | "valor-menor");
                  setPaginaAtual(1);
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Ordenar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recentes">Mais Recentes</SelectItem>
                    <SelectItem value="antigos">Mais Antigos</SelectItem>
                    <SelectItem value="cliente-az">Cliente (A-Z)</SelectItem>
                    <SelectItem value="cliente-za">Cliente (Z-A)</SelectItem>
                    <SelectItem value="valor-maior">Maior Valor</SelectItem>
                    <SelectItem value="valor-menor">Menor Valor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Contador de resultados */}
            {searchTerm || statusFilter !== "all" || (mesFilter && mesFilter !== "all") || (anoFilter && anoFilter !== "all") ? (
              <div className="mt-3 text-sm text-muted-foreground">
                {orcamentosOrdenados.length} {orcamentosOrdenados.length === 1 ? 'orçamento encontrado' : 'orçamentos encontrados'}
                {(searchTerm || statusFilter !== "all" || (mesFilter && mesFilter !== "all") || (anoFilter && anoFilter !== "all")) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSearchTerm("");
                      setStatusFilter("all");
                      setMesFilter("all");
                      setAnoFilter("all");
                      setPaginaAtual(1);
                    }}
                    className="ml-2 h-auto p-0 text-xs"
                  >
                    Limpar filtros
                  </Button>
                )}
              </div>
            ) : null}
          </CardContent>
        </Card>

        <div className="grid gap-4">
          {isLoading && (
            <Card className="border-border animate-shimmer">
              <CardContent className="h-24" />
            </Card>
          )}
          {!isLoading && safeQuotes.length === 0 && (
            <Card className="border-border">
              <CardContent className="text-center py-8">
                <p className="text-muted-foreground">Nenhum orçamento encontrado</p>
                <Button asChild className="mt-4">
                  <Link to="/orcamentos/novo">Criar Primeiro Orçamento</Link>
                </Button>
              </CardContent>
            </Card>
          )}
          {!isLoading && safeQuotes.length > 0 && filteredQuotes.length === 0 && (
            <Card className="border-border">
              <CardContent className="text-center py-8">
                <p className="text-muted-foreground">Nenhum orçamento encontrado com os filtros aplicados</p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm("");
                    setStatusFilter("all");
                  }}
                  className="mt-4"
                >
                  Limpar filtros
                </Button>
              </CardContent>
            </Card>
          )}
          {orcamentosPaginados.map((quote) => (
            <Card
              key={quote.id}
              className="border-2 border-gray-200 hover:border-purple-300 hover:shadow-xl transition-all duration-300 animate-fade-in rounded-xl overflow-hidden group hover:scale-[1.01]"
            >
              <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors flex-shrink-0">
                        <FileText className="w-5 h-5 text-purple-600" />
                      </div>
                      <CardTitle className="text-lg font-bold text-foreground group-hover:text-purple-600 transition-colors">
                        {quote.client || "Cliente não informado"}
                      </CardTitle>
                    </div>
                    <p className="text-sm text-muted-foreground font-mono">{quote.id}</p>
                  </div>
                  <Badge variant="outline" className={`${getStatusColor(quote.status)} text-xs font-bold px-3 py-1`}>
                    {translateStatus(quote.status)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <p className="text-foreground font-medium text-sm">{quote.description}</p>
                  
                  <div className="grid grid-cols-3 gap-4 py-4 border-y border-gray-200">
                    <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                      <DollarSign className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs text-muted-foreground font-semibold uppercase">Valor</p>
                        <p className="text-sm font-bold text-foreground">
                          {formatCurrency(Number(quote.total_value || 0))}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                      <DollarSign className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs text-muted-foreground font-semibold uppercase">C/ Desconto</p>
                        <p className="text-sm font-bold text-foreground">
                          {formatCurrency(Number(quote.value_with_discount || quote.total_value || 0))}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                      <Calendar className="w-5 h-5 text-orange-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs text-muted-foreground font-semibold uppercase">Data</p>
                        <p className="text-sm font-bold text-foreground">
                          {isoDateToBR(quote.date) || "—"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Ações - Agrupadas em uma única linha responsiva */}
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-blue-400 text-blue-600 hover:bg-blue-50 hover:scale-105 transition-all duration-200 font-medium text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
                      onClick={() => navigate(`/orcamentos/${quote.id}/visualizar`)}
                    >
                      <Eye className="w-4 h-4" />
                      <span className="hidden sm:inline ml-1">Visualizar</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-pink-400 text-pink-600 hover:bg-pink-50 hover:scale-105 transition-all duration-200 font-medium text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
                      onClick={() => openPublicView(quote.id)}
                    >
                      <Share2 className="w-4 h-4" />
                      <span className="hidden sm:inline ml-1">Abrir</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-gray-400 text-gray-600 hover:bg-gray-100 hover:scale-105 transition-all duration-200 font-medium text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
                      onClick={() => handlePrintQuote(quote.id)}
                      disabled={isMarkingPDF}
                    >
                      <Printer className="w-4 h-4" />
                      <span className="hidden sm:inline ml-1">{isMarkingPDF ? "Marcando..." : "Imprimir"}</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-green-400 text-green-600 hover:bg-green-50 hover:scale-105 transition-all duration-200 font-medium text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openWhatsApp(quote);
                      }}
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span className="hidden sm:inline ml-1">WhatsApp</span>
                      <span className="sm:hidden">WA</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-purple-400 text-purple-600 hover:bg-purple-50 hover:scale-105 transition-all duration-200 font-medium text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
                      onClick={() => {
                        duplicateQuote(quote).catch(error => {
                          console.error("Erro ao duplicar orçamento:", error);
                          toast.error("Erro ao duplicar orçamento");
                        });
                      }}
                    >
                      <Copy className="w-4 h-4" />
                      <span className="hidden sm:inline ml-1">Duplicar</span>
                    </Button>
                    <PermissionGuard module="orcamentos" action="edit">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-purple-400 text-purple-600 hover:bg-purple-50 hover:scale-105 transition-all duration-200 font-medium text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
                        onClick={() => handleEditQuote(quote.id)}
                      >
                        <Edit className="w-4 h-4" />
                        <span className="hidden sm:inline ml-1">Editar</span>
                      </Button>
                    </PermissionGuard>
                    <Button
                      size="sm"
                      className={`bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 hover:scale-105 transition-all duration-200 text-white font-medium text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2 ${
                        quote.status === 'approved' ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      onClick={() => {
                        if (quote.status === 'approved') {
                          toast.info("Orçamento já foi aprovado");
                          return;
                        }
                        handleApproveQuote(quote);
                      }}
                      disabled={approvingQuote === (quote.code || quote.id) || quote.status === 'approved'}
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span className="hidden sm:inline ml-1">
                        {quote.status === 'approved' ? 'Aprovado' : (approvingQuote === (quote.code || quote.id) ? "Aprovando..." : "Aprovar")}
                      </span>
                      <span className="sm:hidden">
                        {quote.status === 'approved' ? 'OK' : (approvingQuote === (quote.code || quote.id) ? "..." : "OK")}
                      </span>
                    </Button>
                    <PermissionGuard module="orcamentos" action="delete">
                      <Button
                        size="sm"
                        className="bg-red-500 hover:bg-red-600 hover:scale-105 transition-all duration-200 text-white font-medium text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
                        onClick={() => handleDeleteQuote(quote.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="hidden sm:inline ml-1">Excluir</span>
                      </Button>
                    </PermissionGuard>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Paginação */}
        {orcamentosOrdenados.length > itensPorPagina && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 sm:p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border-2 border-gray-200 mt-8">
            <div className="text-xs sm:text-sm font-medium text-muted-foreground text-center sm:text-left">
              Mostrando <span className="text-purple-600 font-bold">{indiceInicio + 1} a {Math.min(indiceFim, orcamentosOrdenados.length)}</span> de <span className="text-purple-600 font-bold">{orcamentosOrdenados.length}</span> orçamentos
            </div>
            <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPaginaAtual(prev => Math.max(prev - 1, 1))}
                disabled={paginaAtual === 1}
                className="border-purple-400 text-purple-600 hover:bg-purple-100 hover:scale-105 transition-all duration-200 font-medium disabled:opacity-50 text-xs sm:text-sm px-2 sm:px-4 py-1 sm:py-2 whitespace-nowrap"
              >
                ← Anterior
              </Button>

              {/* Números de páginas - mostrar até 7 no desktop, 3 no mobile */}
              <div className="flex gap-0.5 sm:gap-1">
                {Array.from({ length: totalPaginas }, (_, i) => i + 1)
                  .filter(page => {
                    if (totalPaginas <= 7) return true;
                    const intervalo = window.innerWidth < 640 ? 1 : 3;
                    const inicio = Math.max(1, paginaAtual - intervalo);
                    const fim = Math.min(totalPaginas, paginaAtual + intervalo);
                    return page >= inicio && page <= fim;
                  })
                  .map((page, index, array) => (
                    <div key={page}>
                      {index > 0 && array[index - 1] !== page - 1 && <span className="px-1 text-muted-foreground text-xs">...</span>}
                      <Button
                        variant={paginaAtual === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          setPaginaAtual(page);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={paginaAtual === page ? "bg-purple-600 hover:bg-purple-700 scale-105 shadow-lg text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2" : "border-purple-300 text-purple-600 hover:bg-purple-50 hover:scale-105 transition-all duration-200 text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"}
                      >
                        {page}
                      </Button>
                    </div>
                  ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPaginaAtual(prev => Math.min(prev + 1, totalPaginas))}
                disabled={paginaAtual === totalPaginas}
                className="border-purple-400 text-purple-600 hover:bg-purple-100 hover:scale-105 transition-all duration-200 font-medium disabled:opacity-50 text-xs sm:text-sm px-2 sm:px-4 py-1 sm:py-2 whitespace-nowrap"
              >
                Próxima →
              </Button>
            </div>
          </div>
        )}

        {/* Modal do WhatsApp */}
        <Dialog open={whatsappModalOpen} onOpenChange={setWhatsappModalOpen}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Personalizar Mensagem do WhatsApp</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {selectedQuote && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">Orçamento:</p>
                  <p className="font-medium">{selectedQuote.id} - {selectedQuote.client}</p>
                  <p className="text-sm text-gray-500">
                    Valor: {formatCurrency(Number(selectedQuote.total_value || 0))}
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="whatsapp-message">Mensagem</Label>
                <Textarea
                  id="whatsapp-message"
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Digite sua mensagem personalizada..."
                  rows={8}
                  className="resize-none"
                />
              </div>
              
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setWhatsappModalOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={() => selectedQuote && generateDefaultMessage(selectedQuote).then(setCustomMessage)}
                  variant="outline"
                >
                  Usar Modelo Padrão
                </Button>
                <Button
                  onClick={sendWhatsAppMessage}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Enviar WhatsApp
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
